import { zodResolver } from '@hookform/resolvers/zod';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ServiceDeal } from 'services/service.deal';
import ServiceEventTracking from 'services/service.event-tracking';
import {
  CompaniesFormFieldsValidationSchema,
  FORM_FIELDS_DEFAULT_VALUES
} from './Form.constants';
import { CompaniesFormFields, UseCompaniesForm } from './Form.types';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export const useCompaniesForm: UseCompaniesForm = () => {
  const { t } = useI18n('companies2');
  const { locale } = useRouter();
  const { addErrorToast, addSuccessToast } = useToasts();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const formMethods = useForm<CompaniesFormFields>({
    defaultValues: FORM_FIELDS_DEFAULT_VALUES,
    resolver: zodResolver(CompaniesFormFieldsValidationSchema)
  });

  const isTermsAccepted = !!formMethods.watch('terms');

  const onSubmit = useCallback(
    async (data: CompaniesFormFields) => {
      try {
        if (!executeRecaptcha) {
          console.error('Recaptcha not available');
          return;
        }

        const recaptchaToken = await executeRecaptcha('talent_engine_v3_form');
        await ServiceDeal.create({
          ...data,
          howDoYouKnowUs: data.howDoYouKnowUs || 'OTHER',
          source: 'Manfred Web',
          consultingOptions: [],
          recaptchaToken
        });

        formMethods.reset(FORM_FIELDS_DEFAULT_VALUES);
        addSuccessToast(t('companies.form.success'));
      } catch (ex) {
        console.error(
          `There was an error while submitting the Companies form. Form data: ${data}. Exception: ${ex}`
        );

        addErrorToast(t('companies.form.error'));
      }
    },
    [addErrorToast, addSuccessToast, formMethods, executeRecaptcha, t]
  );

  const onFormFieldFocus = useCallback(() => {
    ServiceEventTracking.track({
      type: 'Landing - Form Focused',
      properties: {
        objectName: 'contacto-empresas',
        url: window.location.href,
        urlParams: new URLSearchParams(window.location.search).toString()
      }
    });
  }, []);

  return {
    t,
    locale,
    formMethods,
    onSubmit,
    isTermsAccepted,
    onFormFieldFocus
  };
};
